import { CollectionState } from './CollectionState';
import { addEventListener, TypedEmitter, withoutIrrelevant } from '../util';
import {
  cairoComponentSorted,
  cairoFilterToggled,
  cairoAllFiltersCleared,
  cairoSearchResults,
  loadMore,
} from '@wix/bex-utils/@wix/bi-logger-os-data/v2';
import { FiltersMap } from '../model';
import { ReportBI } from '../services';
import { action } from 'mobx';
import { EventEmitter } from 'events';

const withoutDefaults = withoutIrrelevant<
  | 'currentTab'
  | 'currentView'
  | 'currentFilters'
  | 'currentSortOrder'
  | 'listSize'
  | 'initialItems'
>();

export interface CollectionBIReporterParams<T, F extends FiltersMap> {
  readonly collection: CollectionState<T, F>;
  readonly reportBi: ReportBI;
}

export class CollectionBIReporter<T, F extends FiltersMap> {
  readonly collection;
  readonly reportBi;
  readonly events = new EventEmitter() as TypedEmitter<{
    sortStart: (params: { col: { id: string } }) => unknown;
  }>;

  sortedColumnId = '';

  constructor(params: CollectionBIReporterParams<T, F>) {
    this.collection = params.collection;
    this.reportBi = params.reportBi;
  }

  init() {
    const { collection, events } = this;
    const { emitter, query } = collection;

    const disposers = [
      addEventListener(
        emitter,
        'beforeClearFilters',
        action(({ origin }) => {
          this.reportBi(
            withoutDefaults(cairoAllFiltersCleared)({
              filterNames: collection.query.activeFilters
                .map((filter) => filter.name)
                .join(','),
              filteredListSize: collection.result.total,
              numFiltersCleared: collection.query.activeFiltersCount,
              origin,
            }),
          );
        }),
      ),

      addEventListener(
        emitter,
        'beforeSortStart',
        action(({ origin }) => {
          const prevSortedColumnId = this.sortedColumnId;
          const prevSortedIds = new Set(
            query.sort.value.map(({ field }) => field),
          );

          const prevSortOrder = this._getSortOrder();
          let prevSortingOrder = this._getSortDirection(prevSortedColumnId);

          events.once('sortStart', ({ col }) => {
            this.sortedColumnId = col.id;

            if (prevSortedColumnId && col.id !== prevSortedColumnId) {
              prevSortingOrder = this._getSortDirection(prevSortedColumnId);
            } else if (!prevSortedColumnId) {
              prevSortingOrder = '';
            }

            const itemIndex = this.collection.query.sort.value.findIndex(
              ({ field }) => field === col.id,
            );

            this.reportBi(
              withoutDefaults(cairoComponentSorted)({
                newSortedColumn: col.id,
                newSortingOrder: this._getSortDirection(col.id),
                prevSortedColumn: prevSortedColumnId,
                prevSortingOrder,
                origin,
                prevSortOrder,
                newSortOrder: this._getSortOrder(),
                itemIndex,
                isAddNewSort: !prevSortedIds.has(col.id),
                prevNumSortingColumns: prevSortedIds.size,
                numSortingColumns: query.sort.value.length,
              }),
            );
          });
        }),
      ),

      addEventListener(emitter, 'sortStart', (params) => {
        events.emit('sortStart', params);
      }),

      addEventListener(emitter, 'newPageStart', () => {
        const startTime = performance.now();

        return ({ totalNewItems }) => {
          this.reportBi(
            withoutDefaults(loadMore)({
              loadedItems: totalNewItems,
              loadingTime: Math.round(performance.now() - startTime),
            }),
          );
        };
      }),

      addEventListener(emitter, 'searchStart', () => {
        const startTime = performance.now();
        const searchTerm = collection.query.search.value;
        return () => {
          this.reportBi(
            withoutDefaults(cairoSearchResults)({
              searchTerm,
              searchResultsCnt: collection.result.asComputed.total,
              loadingTime: Math.round(performance.now() - startTime),
            }),
          );
        };
      }),

      addEventListener(
        emitter,
        'search',
        action(() => {
          this.reportBi(
            withoutDefaults(cairoFilterToggled)({
              filterName: 'search',
              origin: 'toolbar',
              numFiltersActive: collection.query.activeFiltersCount,
              actionType: 'add',
              filterValue: collection.query.search.value,
              filteredListSize: collection.result.total,
            }),
          );
        }),
      ),
    ];

    return () => {
      disposers.forEach((d) => d());
    };
  }

  _getSortDirection(columndId?: string) {
    const {
      query: {
        sort: { value },
      },
    } = this.collection;

    const sort = value.find(({ field }) => field === columndId);

    if (sort) {
      return sort.direction;
    }

    return 'removed';
  }

  _getSortOrder() {
    const {
      query: { sort },
    } = this.collection;

    return JSON.stringify(
      sort.value.map(({ field, direction }) => ({
        fieldName: field,
        order: direction,
      })),
    );
  }
}
